// © Microsoft Corporation. All rights reserved.
import { logger } from "./Logger";
/**
 * Extension of Map with option to execute callbacks when a specific entry is added/updated
 * @class -  SubscribableMap
 */
export class SubscribableMap extends Map {
    constructor() {
        super(...arguments);
        /** Subscription map */
        this.subscriptions = new Map();
    }
    /**
     * Set function that adds subscription info
     * @param key - the key
     * @param value - the value
     */
    set(key, value) {
        super.set(key, value);
        const subscriptionCallbacks = this.subscriptions.get(key) || [];
        if (!subscriptionCallbacks.length) {
            return this;
        }
        subscriptionCallbacks.forEach((callback, index) => {
            try {
                // Unsubscribe sets the callback to null, check before executing
                if (callback) {
                    callback(value);
                }
            }
            catch (ex) {
                logger.error(`Error executing subscription callback for key [${key}] at handler index [${index}]. Error: ${ex}`);
            }
        });
        return this;
    }
    /**
     * Registers a function that executes when the map entry is already present, newly added or update.
     * @param key - the cache key
     * @param callback - the callback to execute when the cache entry is added/updated
     */
    subscribe(key, callback) {
        const subscription = this.subscribeInternal(key, callback);
        // If the value is already present, invoke the callback immediately
        if (this.has(key)) {
            // Wrapping in a setTimeout to return the subscription first and then start callback execution
            setTimeout(() => {
                this.tryExecuteSubscriptionCallback(key, subscription.index, this.get(key));
            });
        }
        return {
            unsubscribe: subscription.unsubscribe
        };
    }
    /**
     * Registers a function that executes when all keys are present in the map.
     * @param keys - array of cache keys
     * @param callback - the callback to execute when cache entries for all the keys are present
     */
    subscribeMultiple(keys, callback) {
        if (!keys || !keys.length) {
            return;
        }
        if (keys.length === 1) {
            return this.subscribe(keys[0], (data) => callback([data]));
        }
        const internalSubscriptions = keys.map(key => this.subscribeInternal(key, () => {
            const data = [];
            for (const key of keys) {
                if (!this.has(key)) {
                    return;
                }
                data.push(this.get(key));
            }
            if (data.length === keys.length) {
                callback(data);
            }
        }));
        // Run the internal callback once to check if data is already present
        // Wrapping in a setTimeout to return the subscription first and then start callback execution
        setTimeout(() => {
            // Callback is the same for all keys, execute only the first
            if (internalSubscriptions && internalSubscriptions.length) {
                this.tryExecuteSubscriptionCallback(keys[0], internalSubscriptions[0].index);
            }
        });
        return {
            unsubscribe: () => {
                internalSubscriptions.forEach(subscription => subscription && subscription.unsubscribe && subscription.unsubscribe());
            }
        };
    }
    /**
     * Internal subscribe implementation
     * @param key  - the cache key
     * @param callback - the callback to execute when the cache entry is added/updated
     * @returns object containing unsubscribe callback and index of the callback in the subscription array
     */
    subscribeInternal(key, callback) {
        const subscriptions = this.subscriptions.get(key) || [];
        this.subscriptions.set(key, [...subscriptions, callback]);
        const index = subscriptions.length;
        return {
            unsubscribe: () => {
                const callbacks = this.subscriptions.get(key) || [];
                if (callbacks[index]) {
                    callbacks[index] = null;
                }
                this.subscriptions.set(key, callbacks);
            },
            index
        };
    }
    /**
     * Retrieves and executes the subscription callback if it exists
     * @param key subscription key
     * @param index index of the callback function in the subscriptions array
     * @param data optional data for the callback
     */
    tryExecuteSubscriptionCallback(key, index, data) {
        // Always read from subscriptions Map to make sure unsubscribed subscription callbacks are not executed
        const subscriptions = this.subscriptions.get(key);
        if (!subscriptions || !subscriptions.length) {
            return;
        }
        const callback = subscriptions[index];
        if (typeof callback === "function") {
            callback(data);
        }
    }
}
//# sourceMappingURL=SubscribableMap.js.map